import { Avatar, Box, Menu, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { LeadResponse } from '../../services/responses/lead.response';
import { getCrmColors, inkOver } from '../../crm-theme';
import { getInitials } from './crm-lead-owner-avatar';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import { assignableUsers } from '../../filters/crm-assignable-users';

/// Menus de trocar responsável e status, iguais no card e na lista (duas cópias divergiriam).

interface AssignProps {
    lead: LeadResponse;
    users: UserResponse[];
    anchorEl: HTMLElement | null;
    onClose: () => void;
    /// `null` devolve o lead para a fila, sem dono.
    onSelect: (userId: string | null) => void;
}

export const LeadAssignMenu = ({ lead, users, anchorEl, onClose, onSelect }: AssignProps) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { canAssignLeads, userId: currentUserId } = useProfilePermissions();
    const options = assignableUsers(users, lead.assignedUserId, canAssignLeads, currentUserId);

    const select = (userId: string | null) => {
        onClose();

        if (userId !== (lead.assignedUserId ?? null)) onSelect(userId);
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            // O clique no menu não pode chegar ao card nem à linha, que abrem o lead.
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            PaperProps={{ sx: { bgcolor: cc.menuBg, border: `1px solid ${cc.border}` } }}
        >
            {options.map((user) => (
                <MenuItem
                    key={user.id}
                    selected={user.id === lead.assignedUserId}
                    onClick={() => select(user.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, color: cc.textPrimary }}
                >
                    <Avatar
                        sx={{
                            width: 20,
                            height: 20,
                            fontSize: 9,
                            bgcolor: user.color || cc.accent,
                            color: inkOver(user.color || cc.accent),
                        }}
                    >
                        {getInitials(user.name)}
                    </Avatar>
                    <Typography variant="caption" fontWeight={700}>{user.name}</Typography>
                </MenuItem>
            ))}

            {lead.assignedUserId && canAssignLeads && (
                <MenuItem onClick={() => select(null)} sx={{ color: cc.textSecondary, gap: 1 }}>
                    <Typography variant="caption" fontWeight={700}>
                        {t('crmKanban.devolverParaFila')}
                    </Typography>
                </MenuItem>
            )}
        </Menu>
    );
};

interface StatusProps {
    lead: LeadResponse;
    statuses: LeadStatusResponse[];
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onSelect: (statusId: string) => void;
}

export const LeadStatusMenu = ({ lead, statuses, anchorEl, onClose, onSelect }: StatusProps) => {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const select = (statusId: string) => {
        onClose();

        if (statusId !== lead.statusId) onSelect(statusId);
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            PaperProps={{
                sx: {
                    bgcolor: cc.menuBg,
                    backgroundImage: `linear-gradient(${alpha(lead.statusColor, 0.18)}, ${alpha(lead.statusColor, 0.18)})`,
                    border: `1px solid ${alpha(lead.statusColor, 0.55)}`,
                },
            }}
        >
            {statuses.map((status) => (
                <MenuItem
                    key={status.id}
                    selected={status.id === lead.statusId}
                    onClick={() => select(status.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, color: cc.textPrimary }}
                >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color }} />
                    <Typography variant="caption" fontWeight={700}>{status.name}</Typography>
                </MenuItem>
            ))}
        </Menu>
    );
};
